import Vue from 'vue'
import Router from 'vue-router'
import { ActiveUser } from '@/services/user';
import { IS_MAINTENANCE, IS_MZ_BRANDING } from '@/constants/config';

// Containers
const TheContainer = () => import('@/containers/TheContainer');
const OpenAccess = () => import('@/containers/OpenAccess');

// Views
const Form = () => import("@/views/Form");
const Unsubscribe = () => import("@/views/Unsubscribe");
const Campaign = () => import("@/views/Campaign");
const CampaignMail = () => import("@/views/CampaignMail");
const Campaigns = () => import("@/views/Campaigns");
const CampaignStats = () => import("@/views/CampaignStats");
const Maintenance = () => import("@/views/Maintenance");

// Views - Pages
const Page404 = () => import('@/views/pages/Page404');
const Page500 = () => import('@/views/pages/Page500');
const Login = () => import('@/views/pages/Login');
const ForgotPassword = () => import('@/views/pages/ForgotPassword');
const ResetPassword = () => import('@/views/pages/ResetPassword');
const Register = () => import('@/views/pages/Register');
const PrivacyPolicy = () => import('@/views/pages/PrivacyPolicy');

// Users
const Users = () => import('@/views/users/UsersView');
const User = () => import('@/views/users/UserView');
const UnsubscribedUsers = () => import("@/views/users/UnsubscribedUsers");

// Admin
const Certifications = () => import('@/views/admin/Certifications');
const Fonctions = () => import('@/views/admin/Fonctions');
const Ecoles = () => import('@/views/admin/Ecoles');
const RefTable = () => import('@/views/admin/RefTable');
const Competences = () => import('@/views/admin/Competences');
const Rapport = () => import('@/views/admin/Rapport');
const LinkedinView = () => import('@/views/admin/LinkedinView');
const PBIFiles = () => import('@/views/admin/PBIFiles');
const CaptainDataFiles = () => import('@/views/admin/CaptainDataFiles');

Vue.use(Router);

const router = new Router({
  mode: 'history',
  linkActiveClass: 'active',
  scrollBehavior: () => ({ y: 0 }),
  routes: configRoutes()
});

const isOpenAccess = (route) =>
  route.matched.some((route) => route.meta?.isOpenAccess);

const isFound = (route) => route.matched[0].name !== "NotFound";

router.beforeEach((to, from, next) => {
  if (IS_MAINTENANCE) {
    if (to.query.m) {
      localStorage.setItem('override_maintenance', to.query.m);
    }
    if (to.name === 'Maintenance' || to.name === 'MaintenanceWrapper') {
      return next();
    }
    if (localStorage.getItem('override_maintenance') !== '54e6244dedc44ab2940d4fc63799335b') {
      return next({ name: 'Maintenance' });
    }
  }
  if (to.meta?.getTitle) to.meta.title = to.meta.getTitle(to);
  const isAuthenticated = ActiveUser.get();
  if (!isAuthenticated && !isOpenAccess(to)) {
    if (isFound(to)) {
      localStorage.setItem('pathToLoadAfterLogin', to.path);
    }
    return next({name: IS_MZ_BRANDING ? 'Login' : 'ManagementLogin'});
  }
  return next()
});
export default router

function configRoutes () {
  return [
    {
      path: '/',
      name: 'Home',
      redirect: '/campaigns',
      component: TheContainer,
      meta: {
        label: "Accueil"
      },
      children: [
        {
          path: 'unsubscribed',
          name: 'Unsubscribed Users',
          meta: {
            label: "Alumni désinscrits"
          },
          component: UnsubscribedUsers,
        },
        {
          path: 'rapport',
          name: "Génération de rapports",
          component: Rapport,
        },
        {
          path: 'fichiers',
          name: "Fichiers PBI Globaux",
          component: PBIFiles,
        },
        {
          path: 'fichiers-captain-data',
          name: "Fichiers Captain Data",
          component: CaptainDataFiles,
        },
        {
          path: 'linkedin',
          name: "Collecte données réseaux professionnels",
          component: LinkedinView,
        },
        {
          path: 'certifications',
          name: 'Certifications / Titres',
          component: Certifications,
        },
        {
          path: 'fonctions',
          name: 'Fonctions',
          component: Fonctions,
        },
        {
          path: 'competences',
          meta: {
            label: 'Compétences'
          },
          name: 'Competences',
          component: Competences,
        },
        {
          path: 'ecoles',
          name: 'Ecoles',
          component: Ecoles,
        },
        {
          path: "secteur",
          name: "Secteur d'activité",
          component: RefTable,
        },
        {
          path: "specialite",
          name: "Spécialité formation",
          component: RefTable,
        },
        {
          name: 'Campagnes',
          path: "campaigns",
          component: {
            render(c) {
              return c('router-view')
            }
          },
          meta: {
            label: "Campagnes",
          },
          redirect: { name: "Campaigns" },
          children: [
            {
              path: '',
              name: "Campaigns",
              component: Campaigns,
              meta: {
                hideFromBreadcrumb: true,
                label: "Campagnes"
              },
            },
            {
              path: ':campaign_id',
              name: "Campaign",
              meta: {
                paramKey: "currentCampaignName",
              },
              component: {
                render(c) {
                  return c('router-view')
                }
              },
              redirect: to => ({ ...to, name: "CampaignDetails" }),
              children: [
                {
                  path: '',
                  name: "CampaignDetails",
                  component: Campaign,
                  meta: {
                    hideFromBreadcrumb: true,
                    label: "Détails"
                  },
                },
                {
                  path: "statistics",
                  name: "CampaignStats",
                  component: CampaignStats,
                  meta: {
                    label: "Statistiques"
                  },
                },
                {
                  path: 'mail',
                  name: "CampaignMailTemplate",
                  component: CampaignMail,
                  meta: {
                    hideFromBreadcrumb: true,
                    label: "Modèle d'Email"
                  },
                },
              ]
            }
          ]
        }
      ]},
    {
      path: '/admin',
      name: 'Admin',
      redirect: "/admin/unsubscribed",
      component: TheContainer,
      children: [
        {
          path: 'users',
          meta: {
            label: 'Users'
          },
          component: {
            render(c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: '',
              name: 'Users',
              component: Users
            },
            {
              path: ':id',
              meta: {
                label: 'User Details'
              },
              name: 'User',
              component: User
            }
          ]
        },
      ]
    },
    {
      path: "/forms/:formId",
      meta: {
        isOpenAccess: true,
      },
      component: OpenAccess,
      children: [
        {
          path: "",
          name: "Form",
          component: Form,
        },
      ],
    },
    {
      path: "/unsubscribe/:formId",
      meta: {
        isOpenAccess: true,
      },
      component: OpenAccess,
      children: [
        {
          path: "",
          name: "Unsubscribe",
          component: Unsubscribe,
        },
      ],
    },
    {
      path: '/oauth',
      name: 'OAuth Login',
      meta: {
        isOpenAccess: true,
      },
      component: Login,
    },
    {
      path: '/account',
      name: 'account',
      meta: {
        isOpenAccess: true,
      },
      component: {
        render(c) {
          return c('router-view');
        },
      },
      children: [
        {
          path: 'login',
          name: 'Login',
          component: Login,
        },
        {
          path: 'forgot',
          name: 'Forgot password',
          component: ForgotPassword,
        },
        {
          path: 'reset/:token',
          name: 'Password reset',
          component: ResetPassword,
        },
        {
          path: 'login_management',
          name: 'ManagementLogin',
          component: Login,
          props: {
            show_email_login: true
          }
        },
      ],
    },
    {
      path: '/pages',
      redirect: '/pages/404',
      name: 'Pages',
      component: {
        render (c) { return c('router-view') }
      },
      children: [
        {
          path: '404',
          name: 'Page404',
          component: Page404
        },
        {
          path: '500',
          name: 'Page500',
          component: Page500
        },
        {
          path: 'register',
          name: 'Register',
          component: Register
        },
        {
          path: 'privacy_policy',
          name: 'Privacy Policy',
          meta: {
            isOpenAccess: true,
          },
          component: PrivacyPolicy
        }
      ]
    },
    {
      path: '/maintenance',
      name: 'MaintenanceWrapper',
      component: OpenAccess,
      children: [{
        path: '',
        name: 'Maintenance',
        component: Maintenance,
      }],
    },
    {
      path: "*",
      name: 'NotFound',
      component: TheContainer,
      children: [
        { path: '*', component: Page404 }
      ]
    }
  ]
}

